import { Box, Button, FormControl } from '@chakra-ui/react';
import { mapValues, pick } from 'lodash';
import { useRef, useState } from 'react';

import type { SelectionList, TreeItem } from '@revelio/iso-utility';
import { type ChipItem, MultiChipInput } from '@revelio/ui';

import { PlusMinusIcon } from '../../../../shared/components/PlusMinusIcon';
import { BreadcrumbTreeSelection } from '../../../../shared/components/tree/breadcrumb-tree/BreadcrumbTreeSelection';
import { FilterPopover } from '../../../../shared/components/tree/filter-popover/filter-popover';
import type { TreeApi } from '../../../../shared/components/tree/useTreeApi';
import type { SelectionListSelectedItem } from '../../data-builder.store';
import type { SelectionListFilterDefinition } from '../../datasets/deliverable-column-config';
import {
  classifyKey,
  toApiKey,
  toFetchKey,
} from './data-builder-selection-list-mapping';
import { customFilterMultiChipInputCssVars } from './disabled-input-overrides';
import { RequiredHiddenInput } from './RequiredHiddenInput';
import { useDataBuilderSelectionLists } from './useDataBuilderSelectionLists';

type SelectionListFilterValueInputProps = {
  filterDef: SelectionListFilterDefinition;
  selectedItems: SelectionListSelectedItem[];
  onChange: (items: SelectionListSelectedItem[]) => void;
  isDisabled: boolean;
  required?: boolean;
};

function resolveLabel(
  item: SelectionListSelectedItem,
  selectionLists: SelectionList[]
): string {
  // If the label already differs from the id, it was resolved previously.
  if (item.label !== item.id) {
    return item.label;
  }

  const fetchKey = toFetchKey(item.selectionListId);
  const list = selectionLists.find((l) => String(l.id) === String(fetchKey));
  if (!list) return item.label;

  const found = list.value?.find((v) => String(v.id) === String(item.id));
  return found?.label ?? found?.longName ?? found?.shortName ?? item.label;
}

function getGranularitySuffix(selectionListId: string): string {
  const category = classifyKey(toFetchKey(selectionListId));
  if (category !== 'role' && category !== 'skill') return '';

  const match = selectionListId.match(/k(\d+)$/);
  return match ? ` (k${match[1]})` : '';
}

function toChipItems(
  items: SelectionListSelectedItem[],
  selectionLists: SelectionList[]
): ChipItem[] {
  return items.map((item) => ({
    id: item.id,
    label:
      resolveLabel(item, selectionLists) +
      getGranularitySuffix(item.selectionListId),
  }));
}

export function SelectionListFilterValueInput({
  filterDef,
  selectedItems,
  onChange,
  isDisabled,
  required = false,
}: SelectionListFilterValueInputProps) {
  const initialSelections = selectedItems.reduce<Record<string, TreeItem>>(
    (acc, item) => {
      const fetchKey = toFetchKey(item.selectionListId);
      const key = `${fetchKey}.${item.id}`;
      acc[key] = {
        id: item.id,
        item: { id: item.id, label: item.label },
        selectionListId: fetchKey,
        children: [],
      };
      return acc;
    },
    {}
  );
  const [tempSelections, setTempSelections] = useState<
    Record<string, TreeItem>
  >(() => initialSelections);
  const treeRef = useRef<TreeApi>(null);

  const { selectionLists, branchLabels, isV3Job } =
    useDataBuilderSelectionLists({
      apiKeys: filterDef.keys,
    });

  const handleSubmit = () => {
    const items = Object.values(tempSelections)
      .filter((t): t is TreeItem => t !== null && t !== undefined)
      .map(
        (t): SelectionListSelectedItem => ({
          id: String(t.item?.id ?? t.id),
          label: String(t.item?.label ?? t.item?.longName ?? t.id),
          selectionListId: toApiKey(t.selectionListId, isV3Job),
        })
      );
    onChange(items);
  };

  const handleRemove = (chipId: string) => {
    onChange(selectedItems.filter((i) => i.id !== chipId));
  };

  return (
    <Box position="relative">
      <RequiredHiddenInput
        isRequired={required && !isDisabled}
        hasValue={selectedItems.length > 0}
      />
      <FilterPopover
        openHandler={() => {
          setTempSelections(initialSelections);
        }}
        triggerOnClose={() => undefined}
        resetBtn={
          <Button
            variant="ghost"
            size="sm"
            fontSize="12px"
            colorScheme="red"
            flexShrink={0}
            onClick={() => {
              setTempSelections({});
              treeRef.current?.clearAll();
            }}
          >
            Clear Selections
          </Button>
        }
        handleSubmit={handleSubmit}
        menuWidth={360}
        lazy
        isDisabled={isDisabled}
        triggerElement={
          <MultiChipInput
            items={toChipItems(selectedItems, selectionLists)}
            onRemove={handleRemove}
            disabled={isDisabled}
            size="sm"
            cssVars={customFilterMultiChipInputCssVars}
            addSlot={
              <Button
                variant="addcompany"
                leftIcon={<PlusMinusIcon />}
                display="flex"
                isDisabled={isDisabled}
              >
                <span style={{ marginLeft: '-2px' }}>Value</span>
              </Button>
            }
            style={{ cursor: 'pointer' }}
          />
        }
        closeOnBlur={false}
        closeOnOutsideClick
      >
        <FormControl>
          <BreadcrumbTreeSelection
            ref={treeRef}
            initialSelections={initialSelections}
            branches={branchLabels}
            selectionLists={selectionLists}
            height={180}
            onChange={(selections) => {
              setTempSelections(
                mapValues(selections, (tItem) =>
                  pick(tItem, ['id', 'item', 'selectionListId', 'parentId'])
                ) as Record<string, TreeItem>
              );
            }}
          />
        </FormControl>
      </FilterPopover>
    </Box>
  );
}
